import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { getVinPath } from 'site-modules/shared/utils/inventory/vdp-utils';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { buildNewTmvAdjustmentPath, buildNewTmvPricingPath, VehicleEntities, VehicleModel } from './vehicle-v2';
import { InventoryEntities, InventoryModel } from './inventory';

const NO_STYLE = 'no-style';

function buildVinStyleTmvDealPath({ vin, styleId }) {
  return `styles["${styleId || NO_STYLE}"].vins["${vin}"].tmvDeal`;
}

export const InventoryVehiclePaths = {
  buildVinStyleTmvDealPath,
};

export const InventoryVehicleEntities = {
  VinStyleTmvDealData: PropTypes.shape({
    vinStyleId: PropTypes.number,
    tmvDealTypeData: PropTypes.oneOfType([VehicleEntities.NewTmv, InventoryEntities.PriceValidation]),
    tmvAdjustment: VehicleEntities.NewTmvAdjustment,
    mileage: PropTypes.number,
    msrp: PropTypes.number,
    bestDealPrice: PropTypes.number,
    isUsedVin: PropTypes.bool,
    isVinData: PropTypes.bool,
  }),
};

/**
 * Model created for resolving inventory and vehicle models at the same time
 */
export const InventoryVehicleModel = createModelSegment('inventoryVehicle', [
  /**
   * Resolves vinData. If it doesn't have tmv deal type data then resolves tmvData.
   * @see buildVinStyleTmvDealPath
   */
  {
    path: 'styles["{styleId}"].vins["{vin}"].tmvDeal',
    async resolve({ styleId, vin }, context) {
      return context
        .resolveValue(getVinPath(vin), InventoryModel)
        .then(vinData => {
          const vinStyleId = parseInt(get(vinData, 'vehicleInfo.styleInfo.styleId'), 10);

          const vinBasedData = {
            vinStyleId,
            isUsedVin: !!isUsed(get(vinData, 'type')),
            mileage: get(vinData, 'vehicleInfo.mileage'),
            tmvDealTypeData: get(vinData, 'computedDisplayInfo.priceValidation'),
          };

          if (
            !get(vinData, 'computedDisplayInfo.priceValidation.maxFairPrice') ||
            !get(vinData, 'computedDisplayInfo.priceValidation.maxGoodPrice') ||
            !get(vinData, 'computedDisplayInfo.priceValidation.maxGreatPrice') ||
            !get(vinData, 'prices.advertisedPrice')
          ) {
            throw vinBasedData;
          }

          return {
            ...vinBasedData,
            tmvDealTypeData: get(vinData, 'computedDisplayInfo.priceValidation'),
            bestDealPrice:
              (get(vinData, 'prices.edmundsSuggestedPriceIsCompliant', false) &&
                get(vinData, 'prices.edmundsSuggestedPrice', 0)) ||
              0,
            msrp: get(vinData, 'prices.advertisedPrice'),
            isVinData: true,
            vinData: { vin: get(vinData, 'vin'), vid: get(vinData, 'vid') },
          };
        })
        .catch(async ({ vinStyleId, mileage, isUsedVin, tmvDealTypeData }) => {
          if (!vinStyleId && styleId === NO_STYLE) {
            throw new Error();
          }

          if (isUsedVin) {
            return {
              vinStyleId,
              mileage,
              isUsedVin,
              isVinData: false,
            };
          }

          const currentStyleId = vinStyleId || styleId;

          const [tmvData, tmvAdjustment] = await Promise.all([
            context.resolveValue(buildNewTmvPricingPath(currentStyleId), VehicleModel),
            context.resolveValue(buildNewTmvAdjustmentPath(currentStyleId), VehicleModel),
          ]);

          return {
            vinStyleId,
            mileage,
            isUsedVin,
            tmvAdjustment,
            tmvDealTypeData: {
              ...tmvDealTypeData,
              ...tmvData,
            },
            msrp: get(tmvData, 'totalWithOptions.baseMSRP'),
            isVinData: false,
          };
        })
        .catch(() => ({}));
    },
  },
]);
